import { PROJECT_KEYS } from '@/Shared/constants/keys/keys';
import { callOverlay } from '@/Groups/OverlayManager/public/utils/callOverlay';
import store from '@/Shared/store/index';

// Отправка причины принудительного разлогина на бэкенд для аналитики
const reportForcedLogout = (reason) => {
  try {
    const url = `${store.getters.getBackendUrl}/api/auth/report-logout/`;
    const body = JSON.stringify({ reason });
    // keepalive: true — запрос не блокирует навигацию, credentials: 'include' — отправляет cookies
    fetch(url, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
    });
  } catch {
    // Логирование не должно ломать основной флоу
  }
};

export { reportForcedLogout };

const abortExemptEndpoints = [
  '/api/alpha/inner-pages',
  '/api/auth/users/me/',
  '/api/widgets/user-assets/'
];

// Хранилище контроллеров для каждого URL только для fetchWithAuth
const abortControllers = new Map();

// --- BroadcastChannel: межтабовая синхронизация refresh токенов ---
let authChannel = null;
let waitingForRefreshFromOtherTab = false;
let otherTabRefreshPromise = null;

try {
  authChannel = new BroadcastChannel('auth_channel');
  authChannel.onmessage = (event) => {
    const { type, accessToken } = event.data;
    if (type === 'REFRESH_DONE' && accessToken) {
      store.dispatch('saveAccessToken', accessToken);
      if (otherTabRefreshPromise) {
        otherTabRefreshPromise.resolve(true);
        otherTabRefreshPromise = null;
        waitingForRefreshFromOtherTab = false;
      }
    } else if (type === 'REFRESH_FAILED') {
      if (otherTabRefreshPromise) {
        otherTabRefreshPromise.resolve(false);
        otherTabRefreshPromise = null;
        waitingForRefreshFromOtherTab = false;
      }
    } else if (type === 'LOGOUT') {
      store.commit('UserSystem/clearUserData');
      store.dispatch('logout', { silent: true });
    }
  };
} catch {
  // BroadcastChannel не поддерживается — работаем без межтабовой синхронизации
}

export const broadcastMessage = (message) => {
  try {
    authChannel?.postMessage(message);
  } catch {
    // Канал мог быть закрыт
  }
};

export const fetchWithAuth = async (url, options = {}) => {
  const endpointKey = extractEndpointKey(url);

  const isAbortExempt = abortExemptEndpoints.some(exempt => endpointKey.startsWith(exempt));

  if (!isAbortExempt && abortControllers.has(endpointKey)) {
    abortControllers.get(endpointKey).abort();
  }

  const controller = new AbortController();
  abortControllers.set(endpointKey, controller);
  options.signal = controller.signal;

  const accessToken = store.getters.getAccessToken;
  if (accessToken) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`,
    };
  }

  try {
    let response = await fetch(url, options);

    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        // Refresh failed — пользователь уже разлогинен
        abortControllers.delete(endpointKey);
        return response;
      }
      // Новый AbortController для retry — старый signal мог быть отменён
      const retryController = new AbortController();
      abortControllers.set(endpointKey, retryController);
      options.headers['Authorization'] = `Bearer ${store.getters.getAccessToken}`;
      options.signal = retryController.signal;
      response = await fetch(url, options);
    }

    abortControllers.delete(endpointKey);
    return response;
  } catch (error) {
    abortControllers.delete(endpointKey);
    // AbortError — нормальная отмена запроса, не показываем overlay, но выбрасываем ошибку
    if (error.name === 'AbortError') {
      throw error;
    }
    callOverlay({ text: 'Сервер временно недоступен. Пожалуйста, попробуйте позже', color: 'red', icon: 'wifi' });
    console.error('Сервер недоступен:', error);
    throw new Error(`Сервер недоступен: ${error}`);
  }
};

// Токен авторизации
const AUTH_TOKEN = PROJECT_KEYS.AUTH_TOKEN;

export const fetchAIAgentDataWithAuth = async (url, options = {}) => {
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(AUTH_TOKEN && { 'Authorization': `Token ${AUTH_TOKEN}` }),
  };

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers || {}),
    },
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorText}`);
    }

    return response;
  } catch (error) {
    console.error('Error in fetchAIAgentDataWithAuth:', error);
    throw error;
  }
};

// Блокировка для предотвращения параллельных refresh
let isRefreshing = false;
let refreshPromise = null;

const refreshAccessToken = async () => {
  // Если другая вкладка уже делает refresh — ждём результат через BroadcastChannel
  if (waitingForRefreshFromOtherTab && otherTabRefreshPromise) {
    return otherTabRefreshPromise.promise;
  }

  // Если refresh уже идёт в этой вкладке — возвращаем тот же promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  broadcastMessage({ type: 'REFRESH_STARTED' });

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${store.getters.getBackendUrl}/api/token/refresh/`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
      });

      if (!response.ok) {
        // Бэкенд явно требует перелогин (circuit breaker) — сразу logout без retry
        const errorData = await response.json().catch(() => null);
        if (errorData?.relogin_required) {
          reportForcedLogout('relogin_required');
          broadcastMessage({ type: 'REFRESH_FAILED' });
          await store.dispatch('logout');
          broadcastMessage({ type: 'LOGOUT' });
          return false;
        }

        console.error('Refresh token failed, retrying in 2s');
        // Retry через 2 сек — cookie могла обновиться от другой вкладки
        await new Promise(r => setTimeout(r, 2000));
        const retryResponse = await fetch(`${store.getters.getBackendUrl}/api/token/refresh/`, {
          method: 'POST',
          credentials: 'include',
          headers: {'Content-Type': 'application/json'},
        });

        if (!retryResponse.ok) {
          reportForcedLogout('refresh_failed');
          broadcastMessage({ type: 'REFRESH_FAILED' });
          await store.dispatch('logout');
          broadcastMessage({ type: 'LOGOUT' });
          return false;
        }

        const retryData = await retryResponse.json();
        await store.dispatch('saveAccessToken', retryData.access);
        broadcastMessage({ type: 'REFRESH_DONE', accessToken: retryData.access });
        return true;
      }

      const data = await response.json();
      await store.dispatch('saveAccessToken', data.access);
      broadcastMessage({ type: 'REFRESH_DONE', accessToken: data.access });
      return true;
    } catch (error) {
      // Сетевая ошибка — НЕ разлогиниваем, просто сообщаем о неудаче
      console.error('Error refreshing access token:', error);
      broadcastMessage({ type: 'REFRESH_FAILED' });
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Обработка REFRESH_STARTED от другой вкладки
if (authChannel) {
  const originalOnMessage = authChannel.onmessage;
  authChannel.onmessage = (event) => {
    const { type } = event.data;
    if (type === 'REFRESH_STARTED' && !isRefreshing) {
      waitingForRefreshFromOtherTab = true;
      otherTabRefreshPromise = createDeferredPromise();
      // Таймаут на случай если вкладка-инициатор закрылась
      setTimeout(() => {
        if (waitingForRefreshFromOtherTab) {
          waitingForRefreshFromOtherTab = false;
          if (otherTabRefreshPromise) {
            otherTabRefreshPromise.resolve(false);
            otherTabRefreshPromise = null;
          }
        }
      }, 10000);
    }
    originalOnMessage?.(event);
  };
}

function createDeferredPromise() {
  let resolve;
  const promise = new Promise(r => { resolve = r; });
  return { promise, resolve };
}

export function getCookie(name) {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const cookieValue = parts.pop().split(';').shift();
      return cookieValue ? decodeURIComponent(cookieValue) : null;
    }
    return null;
  } catch (error) {
    console.error(`Error getting cookie ${name}:`, error);
    return null;
  }
}

export function setCookie(name, value, days, options = {}) {
  try {
    const {
      path = '/',
      domain = '',
      secure = true,
      sameSite = 'Lax'
    } = options;

    let cookieString = `${name}=${encodeURIComponent(value || '')}`;

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      cookieString += `; expires=${date.toUTCString()}`;
    }

    cookieString += `; path=${path}`;

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += '; Secure';
    }

    cookieString += `; SameSite=${sameSite}`;

    document.cookie = cookieString;
  } catch (error) {
    console.error(`Error setting cookie ${name}:`, error);
  }
}

export function deleteCookie(name, options = {}) {
  try {
    const {
      path = '/',
      domain = '',
      secure = true,
      sameSite = 'Lax'
    } = options;

    let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

    cookieString += `; path=${path}`;

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += '; Secure';
    }

    cookieString += `; SameSite=${sameSite}`;

    document.cookie = cookieString;
  } catch (error) {
    console.error(`Error deleting cookie ${name}:`, error);
  }
}

const extractEndpointKey = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname; // только путь, без query
  } catch (e) {
    console.warn('Invalid URL in fetchWithAuth:', url);
    return url; // fallback — пусть будет как есть
  }
}
